import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, DollarSign } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatNumber } from "@/lib/utils";

export const Route = createFileRoute("/_auth/admin/billing")({
  component: BillingStatsPage,
});

function BillingStatsPage() {
  const stats = useQuery({
    queryKey: ["admin", "billing-stats"],
    queryFn: () => api.admin.billingStats(),
    staleTime: 60_000,
  });

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Métricas de facturación</h1>
        <p className="text-sm text-ink-500 mt-1">MRR, distribución por plan y estado.</p>
      </header>

      {stats.isLoading || !stats.data ? (
        <Skeleton className="h-60" />
      ) : (
        <>
          <Card className="mb-4">
            <div className="flex items-center gap-3">
              <DollarSign size={28} className="text-good" />
              <div>
                <p className="text-xs text-ink-500">MRR (USD)</p>
                <p className="text-3xl font-bold text-ink-900 dark:text-white">
                  ${formatNumber(stats.data.mrr_usd)}
                </p>
                <p className="text-[11px] text-ink-500 mt-1">Monthly Recurring Revenue agregado de todas las cuentas activas con plan</p>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Por estado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {stats.data.accounts_by_status.map((s) => (
                  <div key={s.status} className="flex items-center justify-between">
                    <Badge variant={s.status === "active" ? "good" : s.status === "suspended" ? "bad" : "muted"}>
                      {s.status}
                    </Badge>
                    <span className="font-mono text-sm">{formatNumber(s.count)} cuentas</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Por plan</CardTitle>
                <CardDescription>Plan asignado a cada cuenta</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {stats.data.accounts_by_plan.map((p, i) => (
                  <div key={`${p.plan_code ?? "no-plan"}-${i}`} className="flex items-center justify-between">
                    <div>
                      <Badge>{p.plan_name ?? "(sin plan)"}</Badge>
                      {p.price_cents > 0 && (
                        <span className="text-[11px] text-ink-500 ml-2">
                          ${(p.price_cents / 100).toFixed(2)} {p.currency}/mes
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-sm">{formatNumber(p.count)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
